/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { LockKeyhole, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CompanyLogo from '@/components/company-logo';
import { Button } from '@/components/buttons';
import { Link } from '@/components/link';
import { Separator } from '@/components/ui/separator';
import SupportPhoneNumber from '@/components/support-phone-number';

export default function CheckoutHeader(): ReactElement {
    const { t } = useTranslation('checkout');
    const { t: tHeader } = useTranslation('header');

    return (
        <header className="bg-background w-full">
            <div className="section-container flex items-center justify-between py-2">
                <div className="flex items-center gap-4">
                    <Link to="/" className="flex-1 lg:flex-0 flex items-center" data-testid="header-logo-checkout">
                        <CompanyLogo title={tHeader('logoAlt')} className="h-5" />
                    </Link>
                    <div className="flex items-center gap-1">
                        <LockKeyhole className="size-4 text-foreground" aria-hidden />
                        <span className="text-xs font-semibold text-foreground whitespace-nowrap">
                            {t('header.secureCheckout')}
                        </span>
                    </div>
                </div>
                <div className="flex items-center lg:gap-6">
                    <SupportPhoneNumber
                        hideCopyAtMobile
                        className="font-semibold hover:bg-transparent hover:opacity-50 hover:no-underline transition-opacity py-2 px-2 lg:px-3"
                    />
                    <Separator orientation="vertical" className="hidden lg:block data-[orientation=vertical]:h-4" />
                    <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="lg:hidden hover:bg-transparent hover:opacity-50 transition-opacity size-auto py-2 px-2">
                        <Link to="/cart" aria-label={t('header.cartAriaLabel')}>
                            <ShoppingCart className="size-4" />
                        </Link>
                    </Button>
                    <Button
                        asChild
                        variant="ghost"
                        className="hidden lg:inline-flex hover:bg-transparent hover:opacity-50 transition-opacity uppercase text-xs font-semibold tracking-wide py-2 px-3 h-auto">
                        <Link to="/cart">{t('header.viewCart')}</Link>
                    </Button>
                </div>
            </div>
        </header>
    );
}
